import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, delay, filter, firstValueFrom, Observable, of, skip, Subject, switchMap, takeUntil } from 'rxjs';
import { Book, BookCategory, BookRequestResponse } from '../models/book.model';
import { BookProvider } from '../providers/book.provider';
import { BookFiltersState, Order } from '../store/book-filters.state';
import { DefaultProjectState } from '../store/default-project.state';
import { Feature, LoggerService } from './logger.service';
import { AppState } from '../store/app.state';
import { differenceInWeeks } from 'date-fns';
import { HttpErrorResponse } from '@angular/common/http';
import { Translatable } from '../models/translation.model';
import { BookService } from './book.service';
import { UserState, UserType } from '../store/user.state';

export enum ShelfType {
  New = 'new',
  Popular = 'popular',
  ContinueReading = 'continue-reading',
  FeaturedActive = 'featured-active',
  Collection = 'collection',
  Featured = 'featured',
  Category = 'category',
  Favorites = 'favorites',
  ReadAgain = 'read-again',
  BedStory = 'bed-story',
  YouMightAlsoLike = 'you-might-also-like',
}

/**
 * The purpose of this service is to keep track & data of all the data in the different book shelves data
 * so that you don't have to call up the api every time.
 */

@Injectable({ providedIn: 'root' })
export class BooksService implements OnDestroy {
  initialized$ = new BehaviorSubject(false);
  isReloading$ = new BehaviorSubject(false);

  // User shelves
  continueReadingBooks$ = new BehaviorSubject<Array<Book>>([]);
  myFavoritesBooks$ = new BehaviorSubject<Array<Book>>([]);
  readItAgainBooks$ = new BehaviorSubject<Array<Book>>([]);

  // You might also like shelf (book details / book complete)
  youMightAlsoLikeBooks$ = new BehaviorSubject<Array<Book>>([]);
  youMightALsoLikeId$ = new BehaviorSubject<number | null>(null);

  // Featured active shelve
  featuredActiveBooks$ = new BehaviorSubject<Array<Book>>([]);
  featuredActiveBooksTitle$ = new BehaviorSubject<undefined | Translatable>(undefined);
  featuredActiveId$ = new BehaviorSubject<number | null>(null);

  // Collections shelf
  collectionBooks$ = new BehaviorSubject<Array<Book>>([]);
  collectionTitle$ = new BehaviorSubject<undefined | Translatable>(undefined);
  collectionId$ = new BehaviorSubject<number | null>(null);

  // General shelves
  featuredBooks$: BehaviorSubject<Array<Book>> = new BehaviorSubject<Array<Book>>([]);
  popularBooks$: BehaviorSubject<Array<Book>> = new BehaviorSubject<Array<Book>>([]);
  newBooks$: BehaviorSubject<Array<Book>> = new BehaviorSubject<Array<Book>>([]);
  bedStory$: BehaviorSubject<Array<Book>> = new BehaviorSubject<Array<Book>>([]);
  // Categories shelves
  categoriesBooks$: BehaviorSubject<Array<BookCategory>> = new BehaviorSubject<Array<BookCategory>>([]);

  private destroyed$ = new Subject<void>();

  private readonly noBooksResponse = { totalCount: 0, count: 0, offset: 0, limit: 0, books: [] };

  private previousOrderBy: Order | null = this.bookFiltersState$.bookFilters$.value.orderBy;

  constructor(
    private booksProvider: BookProvider,
    private userState: UserState,
    private appState$: AppState,
    private bookFiltersState$: BookFiltersState,
    private defaultProjectState$: DefaultProjectState,
    private loggerService: LoggerService,
    private bookService: BookService,
  ) {}

  async initialize(): Promise<void> {
    await this.getUserBooks();
    await this.getWorldReaderBooks();

    // Need to reload the shelves if the project id has changed or if the filters has changed
    combineLatest([this.bookFiltersState$.bookFilters$, this.defaultProjectState$.defaultProject$])
      .pipe(skip(1), takeUntil(this.destroyed$), delay(1))
      .subscribe(async ([bookFilters]) => {
        this.isReloading$.next(true);

        const orderByChanged = this.previousOrderBy !== bookFilters.orderBy;

        await this.getUserBooks();

        if (!orderByChanged) {
          await this.getWorldReaderBooks();
        }

        this.isReloading$.next(false);
      });

    // Listen to bookService.book in order to update the YouMightAlsoLike shelf
    this.bookService.book$.pipe(takeUntil(this.destroyed$)).subscribe(book => void this.getYouMightAlsoLikeBooks(book));

    this.initialized$.next(true);

    return Promise.resolve();
  }

  async updateFavoriteBooks(): Promise<void> {
    if (this.userState.user$.value.userType === UserType.GUEST) {
      this.myFavoritesBooks$.next([]);
    } else {
      // We need to load all the books her because we need this data in the book shelf page
      const books = await firstValueFrom(this.booksProvider.getFavoritesBooks(0, 999)).catch(error => {
        this.loggerService.error(error, {
          context: 'BooksService::updateFavoriteBooks cant load books',
          feature: Feature.SHELF,
          status: error.status,
        });
        return [];
      });

      this.myFavoritesBooks$.next(books);
    }
  }

  async updateContinueReadingBooks(): Promise<void> {
    // We need to load all the books her because we need this data in the book shelf page
    const books = await firstValueFrom(this.booksProvider.getReadingHistory(0, 999)).catch(error => {
      this.loggerService.error(error, {
        context: 'BooksService::updateContinueReadingBooks cant load books',
        feature: Feature.SHELF,
        status: error.status,
      });

      return [];
    });

    this.continueReadingBooks$.next(books || []);
  }

  async updateReadItAgainBooks(): Promise<void> {
    // We need to load all the books her because we need this data in the book shelf page
    const books = await firstValueFrom(this.booksProvider.getFinishedBooks(0, 9999)).catch(error => {
      this.loggerService.error(error, {
        context: 'BooksService::updateReadItAgainBooks cant load books',
        feature: Feature.SHELF,
        status: error.status,
      });

      return [];
    });

    this.readItAgainBooks$.next(books || []);
  }

  getBookRequest(type: ShelfType, categoryId?: number): Observable<Array<Book>> {
    switch (type) {
      // Store features / popular & new shelves data in booksService
      case ShelfType.Featured:
        return this.featuredBooks$;
      case ShelfType.Popular:
        return this.popularBooks$;
      case ShelfType.New:
        return this.newBooks$;
      case ShelfType.BedStory:
        return this.bedStory$;
      case ShelfType.ContinueReading:
        return this.continueReadingBooks$;
      case ShelfType.Favorites:
        return this.myFavoritesBooks$;
      case ShelfType.ReadAgain:
        return this.readItAgainBooks$;
      case ShelfType.FeaturedActive:
        return this.featuredActiveBooks$;
      case ShelfType.Collection:
        return this.collectionBooks$;
      case ShelfType.YouMightAlsoLike:
        return this.youMightAlsoLikeBooks$;
      case ShelfType.Category:
        if (!categoryId) {
          this.loggerService.error('BooksService::getBookRequest', {
            feature: Feature.ACTIVITY,
            context: 'No category ID passed for category shelf',
          });
          return of([]);
        }
        return this.bookFiltersState$.bookFilters$.pipe(
          switchMap(filters => {
            return this.booksProvider.getBooksByCategory(categoryId as number, filters.selectedGrade?.id, filters.selectedLanguage?.code);
          }),
        );
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getPagedBooksRequest(type: ShelfType, categoryId?: number, offset = 0, limit = 20): Observable<BookRequestResponse | undefined> {
    const selectedGradeId = this.bookFiltersState$.bookFilters$.value.selectedGrade?.id;
    const selectedLanguageCode = this.bookFiltersState$.bookFilters$.value.selectedLanguage?.code;
    const isGuest = this.userState.user$.value.userType === UserType.GUEST;

    switch (type) {
      case ShelfType.Featured:
        return this.booksProvider.getFeaturedBooksPaginated(selectedGradeId, selectedLanguageCode, offset, limit);
      case ShelfType.Popular:
        return this.booksProvider.getPopularBooksPaginated(selectedGradeId, selectedLanguageCode, offset, limit);
      case ShelfType.New:
        return this.booksProvider.getNewBooksPaginated(selectedGradeId, selectedLanguageCode, offset, limit);
      case ShelfType.BedStory:
        return this.booksProvider.getBedStoryBooksPaginated(selectedGradeId, selectedLanguageCode, offset, limit);
      case ShelfType.FeaturedActive:
        return this.initialized$.pipe(
          filter(initialized => initialized),
          switchMap(() => {
            return this.featuredActiveId$.value
              ? this.booksProvider.getFeaturedActiveBooksPaginated(Number(this.featuredActiveId$.value), selectedGradeId, selectedLanguageCode, offset, limit)
              : of(this.noBooksResponse);
          }),
        );
      case ShelfType.Collection:
        return this.initialized$.pipe(
          filter(initialized => initialized),
          switchMap(() => {
            return this.collectionId$.value
              ? this.booksProvider.getCollectionBooksPaginated(Number(this.collectionId$.value), selectedGradeId, selectedLanguageCode, offset, limit)
              : of(this.noBooksResponse);
          }),
        );

      case ShelfType.ContinueReading:
        return isGuest ? of(this.noBooksResponse) : this.booksProvider.getReadingHistoryPaginated(offset, limit);
      case ShelfType.Favorites:
        return isGuest ? of(this.noBooksResponse) : this.booksProvider.getFavoritesBooksPaginated(offset, limit);
      case ShelfType.ReadAgain:
        return isGuest ? of(this.noBooksResponse) : this.booksProvider.getFinishedBooksPaginated(offset, limit);
      case ShelfType.YouMightAlsoLike:
        return this.youMightALsoLikeId$.value
          ? this.booksProvider.getYouMightAlsoLikeBooksPaginated(Number(this.youMightALsoLikeId$.value), selectedGradeId, selectedLanguageCode, offset, limit)
          : of(this.noBooksResponse);
      case ShelfType.Category:
        if (!categoryId) {
          console.error('No category Id passed');
        }
        return this.bookFiltersState$.bookFilters$.pipe(
          switchMap(filters => {
            return this.booksProvider.getBooksByCategoryPaginated(categoryId as number, filters.selectedGrade?.id, filters.selectedLanguage?.code, offset, limit);
          }),
        );
    }
  }

  private async getUserBooks(): Promise<void> {
    await this.updateFavoriteBooks();
    await this.updateContinueReadingBooks();
    await this.updateReadItAgainBooks();
  }

  private async getWorldReaderBooks(): Promise<void> {
    // Need to reload the shelves if the project id has changed or if the filters has changed
    const selectedGradeId = this.bookFiltersState$.bookFilters$.value.selectedGrade?.id;
    const selectedLanguageCode = this.bookFiltersState$.bookFilters$.value.selectedLanguage?.code;

    const featuredBooks = await firstValueFrom(this.booksProvider.getFeaturedBooks(selectedGradeId, selectedLanguageCode)).catch(error => {
      this.loggerService.error(error, {
        context: 'BooksService::getWorldReaderBooks - featuredBooks cant load books',
        feature: Feature.SHELF,
        status: error.status,
      });
      return [];
    });

    // Featured active shelf
    const featuredActive = await firstValueFrom(this.booksProvider.getFeaturedActive()).catch((error: HttpErrorResponse) => {
      if (error.status === 404) {
        // Do nothing in this case
        if (this.featuredActiveBooks$) {
          this.featuredActiveId$.next(null);
        }

        this.featuredActiveBooks$.next([]);
      } else {
        this.loggerService.error(error, {
          context: 'BooksService::getWorldReaderBooks - getFeaturedActive cant load books',
          feature: Feature.SHELF,
          status: error.status,
        });
      }
    });

    if (featuredActive) {
      this.featuredActiveBooksTitle$.next(featuredActive.translatableTitle);
      this.featuredActiveId$.next(featuredActive.id);
      const featuredActiveBooks = await firstValueFrom(this.booksProvider.getFeaturedActiveBooks(featuredActive.id, selectedGradeId, selectedLanguageCode)).catch(
        error => {
          this.loggerService.error(error, {
            context: 'BooksService::getWorldReaderBooks - featuredAtiveBooks cant load books',
            feature: Feature.SHELF,
            status: error.status,
          });
          return [];
        },
      );

      this.featuredActiveBooks$.next(featuredActiveBooks || []);
    }

    // Bed story shelf
    const bedStory = await firstValueFrom(this.booksProvider.getBedStoryBooks(selectedGradeId, selectedLanguageCode)).catch(error => {
      this.loggerService.error(error, {
        context: 'BooksService::getWorldReaderBooks - bedStoryBooks cant load books',
        feature: Feature.SHELF,
        status: error.status,
      });
      return [];
    });

    // Collections shelf
    const collection = await firstValueFrom(this.booksProvider.getCollection()).catch((error: HttpErrorResponse) => {
      this.loggerService.error(error, {
        context: 'BooksService::getWorldReaderBooks - collections cant load collection',
        feature: Feature.SHELF,
        status: error.status,
      });
    });

    if (collection) {
      this.collectionTitle$.next(collection.translatableTitle);
      this.collectionId$.next(collection.id);

      const collectionBooks = await firstValueFrom(this.booksProvider.getCollectionBooks(collection.id, selectedGradeId, selectedLanguageCode)).catch(error => {
        this.loggerService.error(error, {
          context: 'BooksService::getWorldReaderBooks - collectionBooks cant load books',
          feature: Feature.SHELF,
          status: error.status,
        });
        return [];
      });

      this.collectionBooks$.next(collectionBooks || []);
    } else {
      this.collectionId$.next(null);
      this.collectionBooks$.next([]);
    }

    /**
     * Popular books & New books (Week based on app.firstLaunch state)
     * Week 1: Books from 1 to 20 (offset: 0)
     * Week 2: Books from 21 to 40 (offset: 20)
     * Week 3: Books from 41 to 60 (offset: 40)
     */

    const offset = [0, 20, 40];
    const diffInWeeks = Math.abs(differenceInWeeks(this.appState$.app$.value.firstAccess, new Date()));
    const offsetToApply = offset[diffInWeeks % 3] || 0;

    const popularBooks = await firstValueFrom(this.booksProvider.getPopularBooks(selectedGradeId, selectedLanguageCode, offsetToApply)).catch(error => {
      this.loggerService.error(error, {
        context: 'BooksService::getWorldReaderBooks - popularBooks cant load books',
        feature: Feature.SHELF,
        status: error.status,
      });

      return [];
    });

    const newBooks = await firstValueFrom(this.booksProvider.getNewBooks(selectedGradeId, selectedLanguageCode)).catch(error => {
      this.loggerService.error(error, {
        context: 'BooksService::getWorldReaderBooks - newBooks cant load books',
        feature: Feature.SHELF,
        status: error.status,
      });
      return [];
    });

    const categoriesBooks: BookCategory[] = await firstValueFrom(this.booksProvider.getBookCategories()).catch(error => {
      this.loggerService.error(error, {
        context: 'BooksService::getWorldReaderBooks - newBooks cant load books',
        feature: Feature.SHELF,
        status: error.status,
      });
      return [];
    });

    this.bedStory$.next(bedStory || []);
    this.categoriesBooks$.next(categoriesBooks || []);
    this.featuredBooks$.next(featuredBooks || []);
    this.popularBooks$.next(popularBooks || []);
    this.newBooks$.next(newBooks || []);
  }

  private async getYouMightAlsoLikeBooks(book: Book | null): Promise<void> {
    const selectedGradeId = this.bookFiltersState$.bookFilters$.value.selectedGrade?.id;
    const selectedLanguageCode = this.bookFiltersState$.bookFilters$.value.selectedLanguage?.code;

    if (!book || book.tags.length === 0) {
      this.youMightAlsoLikeBooks$.next([]);
      this.youMightALsoLikeId$.next(null);
      return;
    }

    this.youMightALsoLikeId$.next(book.tags[0].id);

    const youMightAlsoLikeBooks: Book[] = await firstValueFrom(this.booksProvider.getYouMightAlsoLikeBooks(book.tags[0].id, selectedGradeId, selectedLanguageCode)).catch(
      error => {
        this.loggerService.error(error, {
          context: 'BooksService::getYouMightAlsoLikeBooks - cant load books',
          feature: Feature.SHELF,
          status: error.status,
        });
        return [];
      },
    );

    this.youMightAlsoLikeBooks$.next(youMightAlsoLikeBooks || []);
  }
}
